import styled from 'styled-components';
import { thinScrollbar } from './chatSurfaces.styled';

/**
 * The conversation's own shell: the card, the floating title bar, the scroll
 * area between them and the two things that float over it.
 *
 * There is no scroll container anywhere else in this app - `ContentStyled` is
 * `overflow: hidden` and every list scrolls the window - so this is the one
 * place that pattern exists.
 */

export const ChatThreadWrapper = styled.div<{
  $embedded?: boolean;
  $bare?: boolean;
}>`
  /* Anchors DropOverlay. */
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /*
   * Standalone (partner): exactly one screen tall, minus the gap it keeps above
   * and below - no more, or the document outgrows the viewport and the window
   * scrolls behind an already-scrolling conversation.
   *
   * --app-vh is the VISIBLE height, published by useVisualViewportHeight; see
   * that file for why neither vh nor dvh survives a phone keyboard. 100dvh is
   * the fallback where the API is missing.
   *
   * 100px = a 50px gap top and bottom. Below 800px it is 12px each, and the
   * page cancels the rest of ContentStyled's padding itself.
   */
  height: ${({ $embedded }) =>
    $embedded ? '100%' : 'calc(var(--app-vh, 100dvh) - 100px)'};

  /*
   * What colour the header and composer dissolve into.
   *
   * Both float over the conversation behind a fade, and a fade only disappears
   * if it ends in the colour it is laid on - get it wrong and it paints a pale
   * band across the top and bottom of the thread.
   *
   * Set here ONLY when this component draws the surface itself. $bare means the
   * parent drew it, and the parent is the one that knows what colour it made
   * it: the admin page dropped its card entirely and puts the messages on the
   * app's cream, while the partner's chat bubble draws a white panel. So the
   * variable is left to inherit from them rather than guessed at from $bare.
   *
   * Both ends are published, the second as the same colour at zero alpha. The
   * transparent stop has to be THIS colour: the CSS keyword transparent is
   * transparent BLACK, and ramping to it greys the fade on the way out.
   */
  ${({ theme, $bare }) =>
    !$bare &&
    `
    --chat-fade: ${theme.white};
    --chat-fade-end: ${theme.white}00;
  `}

  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  border-radius: 20px;
  /*
   * Clips to the radius - but only when there IS a radius. The floating header
   * and composer wear a 30px-blur shadow from 16px inside this box, so a clip
   * here slices it off mid-strength and draws a hard line down each edge. That
   * is invisible on the card it was designed against and obvious on the admin
   * page's cream, where the thread has no card at all. Bare means no rounding
   * to clip to, so there is nothing to lose by letting the shadows run out.
   */
  overflow: ${({ $bare }) => ($bare ? 'visible' : 'hidden')};

  @media (max-width: 800px) {
    height: ${({ $embedded }) =>
      $embedded ? '100%' : 'calc(var(--app-vh, 100dvh) - 24px)'};
    border-radius: 16px;
  }

  /* Sharing a card with the inbox: the card outside owns the surface, and a
     second one drawn in here would be a box inside a box. */
  ${({ $bare }) =>
    $bare &&
    `
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
  `}
`;

/**
 * The title bar is the composer's mirror image: a rounded box floating over the
 * conversation, with the same even fade behind it, so the messages run to the
 * top edge of the card and dissolve under it rather than stopping at a rule.
 *
 * Its height is published the same way too - see ComposerSlot.
 */
export const ThreadHeaderSlot = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  /* Its own compositing layer, or iOS paints the scroller's over it and the
     messages run across the bar rather than under it. A z-index alone does not
     settle it there: the two are in different layers, and the scroller's wins
     whatever the stacking order says. */
  transform: translateZ(0);
  /* Even inset: the gap to the side walls matches the gap to the top edge, so
     the box reads as floating in the card rather than pinned to its corners. */
  padding: 16px;
  /* Ends in whatever surface is behind it - see --chat-fade on the wrapper. */
  background: linear-gradient(
    to bottom,
    var(--chat-fade, #ffffff) 0%,
    var(--chat-fade-end, #ffffff00) 100%
  );

  @media (max-width: 800px) {
    padding: 12px;
  }
`;

export const ThreadHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* Left padding carries the text; the right side is trimmed for the button,
     exactly as the composer bar is trimmed for its icons. */
  padding: 4px 4px 4px 16px;
  /* 42, not 40: box-sizing is border-box, so min-height swallows the 1px
     border on each side and a 40 here comes out 2px under the composer and the
     tabs, which are the other two full-width pills on this screen. */
  min-height: 42px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};

  /*
   * Taller on a phone, to match the composer at the other end.
   *
   * The bar's own controls grow there - the back arrow and the two actions go
   * from 16px glyphs to 22px, and the composer's buttons to 40px discs, which
   * puts that bar at 48 of content plus its border. The title bar kept its 40
   * and came out visibly the shorter of the two, which on a screen where they
   * are the only two floating pills is the first thing you notice.
   */
  @media (max-width: 800px) {
    padding: 4px 4px 4px 12px;
    min-height: 50px;
  }
`;

/**
 * Holds the scroll area and anchors the jump-to-latest button and the loading
 * spinner over it.
 *
 * A styled div rather than the shared Div: that one hard-sets height auto below
 * 800px, which collapsed this wrapper on mobile - the conversation lost its
 * height, the composer floated up under the header and the spinner came with
 * it. Flex 1 is the right expression of "take what is left" here anyway.
 */
export const ThreadBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const ChatScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  /*
   * The webkit touch-scrolling property used to be here. It has been the
   * default since iOS 13, and what it still does is put the scroller on its own
   * compositing layer - one iOS paints OVER absolutely positioned siblings, so
   * messages scrolled straight across the floating title bar instead of under
   * it. See the two slots below, which are promoted for the same reason.
   */
  /*
   * Chrome compensates for content inserted above the viewport by itself, and
   * this thread already does that for older history - see the anchoring effect
   * in useChatMessages. Two corrections for one prepend is a double shift, and
   * the reader lands a page away from what they were reading. Turning the
   * browser's off leaves one implementation, and it is the one that also has to
   * exist for Safari, which has no scroll anchoring at all.
   */
  overflow-anchor: none;
  padding: 24px;
  /*
   * Clears the floating header, whose real height ChatThread measures. That
   * height already includes the 16px the slot pads itself with, so this takes
   * the larger of the two rather than adding them: summing left the first
   * message a header's padding plus a full gutter down the page. The default
   * is 0 - partner has no title bar.
   *
   * The matching clearance at the BOTTOM is a spacer element, not padding -
   * see ComposerClearance below.
   */
  padding-top: max(24px, var(--chat-header-height, 0px));
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${thinScrollbar}

  @media (max-width: 800px) {
    padding: 16px;
    padding-top: max(16px, var(--chat-header-height, 0px));
    padding-bottom: 0;
  }
`;

/**
 * The room the last message needs to clear the floating composer.
 *
 * An element rather than `padding-bottom` on the scroll area, because **WebKit
 * does not include a scroll container's bottom padding in its scrollable
 * area**. Chrome and Firefox do, which is why this looked right in a desktop
 * browser's device emulator and wrong on an actual iPhone: on Safari the
 * conversation simply stopped short and left the newest message stuck behind
 * the composer with no way to scroll past it.
 *
 * A child's height is content, and every engine scrolls to the end of its
 * content - so this works the same everywhere instead of relying on the one
 * behaviour the target browser gets wrong.
 */
export const ComposerClearance = styled.div`
  flex: none;
  height: max(24px, var(--chat-composer-height, 72px));

  @media (max-width: 800px) {
    height: max(16px, var(--chat-composer-height, 64px));
  }
`;

/**
 * The composer floats over the conversation instead of taking a row of its own,
 * so the messages run to the bottom edge of the card and scroll under it.
 *
 * Its height is published as a custom property by ChatThread, which is what
 * keeps the scroll area's bottom padding and the jump-to-latest button clear of
 * it - the bar grows with a reply strip, staged files or a multi-line draft, so
 * a fixed number here would either clip the last message or leave a gap.
 */
export const ComposerSlot = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  /* Same promotion as the title bar above, for the same iOS reason. */
  transform: translateZ(0);
`;

/**
 * Covers the whole thread while files are dragged over it, wearing the same
 * dashed red box as the Import Menu dropzone.
 *
 * Kept mounted and faded with opacity rather than mounted on drag: a CSS
 * transition has nothing to animate from on a freshly mounted node, so
 * conditional rendering would make it snap in.
 *
 * `pointer-events: none` is load-bearing, not a nicety: the overlay sits
 * directly under the cursor mid-drag, and if it could receive events the
 * wrapper would immediately see a dragleave and hide it again - a flicker loop
 * for as long as the pointer is held there.
 */
export const DropOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  /* Inset rather than flush to the wrapper edge, so the dashed box reads as a
     drop target sitting on the thread instead of a second border around it. */
  inset: 10px;
  z-index: 3;
  pointer-events: none;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  text-align: center;

  /* Same tint the app already uses for red-on-light surfaces. The last two
     digits keep a hint of the conversation showing through - fully opaque, it
     stops reading as an overlay. */
  background: ${({ theme }) => `${theme.transparentRed}F2`};
  border: 2px dashed ${({ theme }) => theme.primary};
  border-radius: 16px;
  color: ${({ theme }) => theme.primary};
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: ${({ theme }) => theme.transitions.ease200ms};

  /* The limits, stated before the drop rather than as a toast after it. */
  small {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${({ theme }) => theme.lightGray};
  }

  @media (max-width: 800px) {
    font-size: 14px;

    svg {
      width: 40px;
      height: 40px;
    }
  }
`;

/** Floats over the thread when a new message lands while scrolled back. */
export const JumpToLatestButton = styled.button`
  position: absolute;
  /* Above the floating composer, not behind it. */
  bottom: calc(8px + var(--chat-composer-height, 72px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  padding: 8px 18px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.white};
  background: ${({ theme }) => theme.primary};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
`;
